import CURRENCY_LIST from "constants/CURRENCY_LIST";
import { Package, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import SelectList from "types/SelectList";

import { Caixa, getCaixas } from "services/caixas";

import { getPedidos } from "services/realizarCotacao/realizarCotacao.service";
import { Pedido } from "services/realizarCotacao/realizarCotacao.types";
import { PedidoItem } from "services/cotacoes";
import { Campo, RodapeEtapa, Secao, Selecao } from "./campos";

// O que vai dentro de uma caixa. Fica junto da cotação (caixa JSONB) para
// servir de packing list — a invoice da transportadora continua sendo por
// remessa, tanto FedEx quanto UPS só aceitam commodities no nível do envio.
export interface CaixaEnvioItem {
  descricao: string;
  hscode: string;
  qty: number;
  precoUnit: number;
  /**
   * Peso unitario em kg. O peso do volume e a caixa vazia do cadastro mais a
   * soma destes — a caixa guarda so a tara, nunca o peso do que vai dentro.
   */
  pesoUnit: number;
}

export interface CaixaEnvio {
  entryId: string;
  caixaId: string;
  caixa?: Caixa;
  pesoKg: number;
  itens: CaixaEnvioItem[];
}

export interface Estagio1Data {
  currencyPrice: string;
  currencyPay: string;
  pedidoId: string;
  selectedPedido?: Pedido;
  manualItems?: PedidoItem[];
  /**
   * Peso dos PRODUTOS em kg, sem a embalagem. A caixa entra depois, pela tara
   * do cadastro. Antes este campo era lido como "peso total" e so servia de
   * trava para o botao: o valor digitado nunca chegava na cotacao.
   */
  pesoTotal: string;
  caixaId: string;
  selectedCaixa?: Caixa;
  // Multi-volume (cotação manual). Quando vem preenchido, manda uma caixa por
  // item da lista e ignora caixaId/selectedCaixa, que é o caminho de 1 caixa
  // usado pelo fluxo com CSV.
  caixas?: CaixaEnvio[];
  hscodes: string[];
}

type Props = {
  next: () => void;
  data: Estagio1Data;
  setData: React.Dispatch<React.SetStateAction<Estagio1Data>>;
  setShow: (value: React.SetStateAction<boolean>) => void;
  /**
   * O modal está aberto. Os três estágios ficam montados o tempo todo (o
   * estado local se perderia ao desmontar), então sem isto a lista de
   * pedidos era buscada uma única vez na montagem: cancelar uma cotação e
   * voltar aqui mostrava a lista velha, sem o pedido que tinha sido
   * liberado.
   */
  aberto?: boolean;
};

const Estagio1 = ({ setShow, next, data, setData, aberto = true }: Props) => {
  const { t } = useTranslation("dashboard");

  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [caixas, setCaixas] = useState<Caixa[]>([]);
  const [listaPedidos, setListaPedidos] = useState<SelectList>([]);
  const [listaCaixas, setListaCaixas] = useState<SelectList>([]);

  useEffect(() => {
    const load = async () => {
      const [pedidosResp, caixasResp] = await Promise.all([getPedidos(), getCaixas()]);

      // Some da lista só o pedido realmente despachado. Antes o filtro olhava
      // `status`, que vira true no momento em que a cotação é criada — uma
      // emissão que falhava tirava o pedido daqui e obrigava a apagar a cotação
      // para refazer tudo. Fallback para `status` quando o backend for antigo.
      const pendingPedidos = (pedidosResp?.itens ?? []).filter((p) =>
        p.despachado === undefined
          ? p.status === false || p.status == null
          : !p.despachado,
      );

      setPedidos(pendingPedidos);
      setCaixas(caixasResp);

      setListaPedidos(
        pendingPedidos.map((p) => ({
          label: `${p.pedido_ref} - ${p.nomeComprador}`,
          value: String(p.id),
        })),
      );
      setListaCaixas(
        caixasResp.map((c) => ({
          label: `${c.cod_identificacao} · ${Number(c.comprimento)}×${Number(c.largura)}×${Number(c.altura)} cm`,
          value: String(c.id),
        })),
      );
    };
    if (aberto) load();
  }, [aberto]);

  const pedidoSelecionado = useMemo(
    () => pedidos.find((p) => String(p.id) === data.pedidoId),
    [pedidos, data.pedidoId],
  );

  useEffect(() => {
    if (!pedidoSelecionado) return;
    setData((prev) => ({
      ...prev,
      hscodes: pedidoSelecionado.itens.map((i) => i.hscode ?? ""),
    }));
  }, [pedidoSelecionado, setData]);

  /** Peso dos produtos que o pedido importado ja trouxe, por item. */
  const pesoProdutosPedido = useMemo(() => {
    const itens = data.selectedPedido?.itens ?? [];
    return itens.reduce(
      (acc, it) => acc + (Number(it.pesoUnit) || 0) * (Number(it.qty) || 0),
      0,
    );
  }, [data.selectedPedido]);

  // Preenche a partir do pedido, mas mantem editavel: nem toda loja manda peso
  // por produto, e sem o campo a pessoa ficaria sem como cotar.
  useEffect(() => {
    if (pesoProdutosPedido <= 0) return;
    setData((prev) => ({
      ...prev,
      pesoTotal: String(Number(pesoProdutosPedido.toFixed(3))),
    }));
  }, [pesoProdutosPedido, setData]);

  const pesoCaixaVazia = Number(data.selectedCaixa?.peso) || 0;
  const pesoDespacho = (Number(data.pesoTotal) || 0) + pesoCaixaVazia;

  const isValid = useMemo(() => {
    return (
      data.currencyPrice &&
      data.currencyPay &&
      data.pedidoId &&
      data.pesoTotal &&
      Number(data.pesoTotal) > 0 &&
      data.caixaId &&
      data.hscodes.length > 0 &&
      data.hscodes.every((h) => h.trim().length > 0)
    );
  }, [data]);

  const itensTabela = data.selectedPedido?.itens ?? pedidoSelecionado?.itens ?? [];

  const removeItem = (index: number) => {
    setData((prev) => {
      const pedido = prev.selectedPedido
        ? {
            ...prev.selectedPedido,
            itens: prev.selectedPedido.itens.filter((_, i) => i !== index),
          }
        : prev.selectedPedido;
      return {
        ...prev,
        selectedPedido: pedido,
        hscodes: prev.hscodes.filter((_, i) => i !== index),
      };
    });
  };

  const selecionarPedido = (id: string) => {
    const pedido = pedidos.find((p) => p.id === Number(id));
    setData((prev) => ({
      ...prev,
      pedidoId: id,
      selectedPedido: pedido
        ? { ...pedido, itens: Array.isArray(pedido.itens) ? [...pedido.itens] : [] }
        : undefined,
    }));
  };

  const comHsCode = data.hscodes.filter((h) => h?.trim()).length;

  const moeda = (v: unknown) =>
    `${data.currencyPrice} ${Number(v || 0).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  return (
    <>
      <div className="space-y-5">
        {/* Pedido da loja */}
        {data.selectedPedido ? (
          <div className="flex items-center gap-3 rounded-xl border-2 border-brand-primary/30 bg-blue-50/40 px-3 py-2.5">
            <span className="shrink-0 rounded bg-[#008060] px-1.5 py-1 text-[9px] font-bold uppercase text-white">
              Loja
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-slate-900">
                #{data.selectedPedido.pedido_ref} · {data.selectedPedido.nomeComprador}
              </p>
              <p className="truncate text-[11px] text-slate-500">
                {itensTabela.length} {itensTabela.length === 1 ? "item" : "itens"}
                {data.selectedPedido.total ? ` · ${moeda(data.selectedPedido.total)}` : ""}
                {data.selectedPedido.cidade
                  ? ` · ${[
                      data.selectedPedido.cidade,
                      data.selectedPedido.estado,
                      data.selectedPedido.pais,
                    ]
                      .filter(Boolean)
                      .join(", ")}`
                  : ""}
              </p>
            </div>
            <div className="w-44 shrink-0">
              <Selecao
                label=""
                valor={data.pedidoId}
                aoMudar={selecionarPedido}
                opcoes={listaPedidos}
                placeholder="Trocar pedido"
              />
            </div>
          </div>
        ) : (
          <Secao titulo="Pedido da loja">
            <Selecao
              label={t("fields.shopify_order")}
              valor={data.pedidoId}
              aoMudar={selecionarPedido}
              opcoes={listaPedidos}
              placeholder="Escolha o pedido"
            />
          </Secao>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Selecao
            label={t("fields.quotation_currency")}
            valor={data.currencyPrice}
            aoMudar={(v) => setData((p) => ({ ...p, currencyPrice: v }))}
            opcoes={CURRENCY_LIST}
          />
          <Selecao
            label={t("fields.payment_currency")}
            valor={data.currencyPay}
            aoMudar={(v) => setData((p) => ({ ...p, currencyPay: v }))}
            opcoes={CURRENCY_LIST}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Campo
              label={t("fields.products_weight")}
              valor={data.pesoTotal}
              aoMudar={(v) => setData((p) => ({ ...p, pesoTotal: v }))}
              placeholder="1,00"
              sufixo="kg"
              tipo="number"
            />
            <p className="mt-1 text-[11px] text-slate-400">
              {pesoCaixaVazia > 0
                ? `+ ${String(Number(pesoCaixaVazia.toFixed(3))).replace(".", ",")} kg da caixa vazia = ${String(
                    Number(pesoDespacho.toFixed(3)),
                  ).replace(".", ",")} kg no despacho`
                : t("fields.products_weight_hint")}
            </p>
          </div>

          <Selecao
            label={t("fields.package")}
            valor={data.caixaId}
            aoMudar={(id) => {
              const caixa = caixas.find((c) => c.id === Number(id));
              setData((prev) => ({ ...prev, caixaId: id, selectedCaixa: caixa }));
            }}
            opcoes={listaCaixas}
            placeholder="Escolha a embalagem"
          />
        </div>

        {/* Itens */}
        {itensTabela.length > 0 ? (
          <Secao
            titulo="Itens e HS Code"
            extra={
              <span className="text-[11px] text-slate-400">
                Usado na invoice e no cálculo de impostos
              </span>
            }
          >
            <div className="overflow-hidden rounded-xl border border-slate-200">
              <div className="grid grid-cols-[1fr_64px_150px_140px_40px] items-center gap-2 bg-slate-50 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-500">
                <span>Item</span>
                <span className="text-center">Qtd</span>
                <span>HS Code / NCM</span>
                <span>Valor unit.</span>
                <span />
              </div>

              {itensTabela.map((item, index) => (
                <div
                  key={index}
                  className="grid grid-cols-[1fr_64px_150px_140px_40px] items-center gap-2 border-t border-slate-100 px-3 py-2"
                >
                  <span className="truncate text-sm text-slate-700" title={item.titulo}>
                    {item.titulo}
                  </span>
                  <span className="text-center text-sm text-slate-600">
                    {item.qty ?? 1}
                  </span>
                  <input
                    value={data.hscodes[index] ?? ""}
                    onChange={(e) =>
                      setData((prev) => {
                        const v = e.target.value;
                        const clone = [...prev.hscodes];
                        clone[index] = v;
                        const pedido = prev.selectedPedido
                          ? {
                              ...prev.selectedPedido,
                              itens: (prev.selectedPedido.itens || []).map((it, i) =>
                                i === index ? { ...it, hscode: v } : it,
                              ),
                            }
                          : prev.selectedPedido;
                        return { ...prev, hscodes: clone, selectedPedido: pedido };
                      })
                    }
                    placeholder="6104.63.00"
                    className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
                  />
                  <div className="relative">
                    <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[10px] text-slate-400">
                      {data.currencyPrice}
                    </span>
                    <input
                      value={String(
                        data.selectedPedido?.itens?.[index]?.preco ?? item.preco ?? "",
                      )}
                      onChange={(e) =>
                        setData((prev) => {
                          const valor = e.target.value.replace(/[^\d.]/g, "").slice(0, 10);
                          const pedido = prev.selectedPedido
                            ? {
                                ...prev.selectedPedido,
                                itens: (prev.selectedPedido.itens || []).map((it, i) =>
                                  i === index ? { ...it, preco: Number(valor || 0) } : it,
                                ),
                              }
                            : prev.selectedPedido;
                          return { ...prev, selectedPedido: pedido };
                        })
                      }
                      placeholder="30"
                      className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-2 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-300 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeItem(index)}
                    aria-label="Remover item"
                    className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 text-red-500 transition-colors hover:border-red-200 hover:bg-red-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </Secao>
        ) : (
          <p className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-slate-200 px-4 py-8 text-center text-xs text-slate-400">
            <Package className="h-4 w-4 shrink-0" />
            Escolha um pedido da loja para carregar os itens.
          </p>
        )}
      </div>

      <RodapeEtapa
        onCancelar={() => setShow(false)}
        onAvancar={next}
        avancarLiberado={Boolean(isValid)}
        rotuloAvancar="Continuar para dados"
        dica={
          isValid
            ? `${comHsCode} ${comHsCode === 1 ? "item" : "itens"} com HS Code preenchido`
            : "Preencha o HS Code de todos os itens"
        }
      />
    </>
  );
};

export default Estagio1;
